/* Oshea PLP — filters, sort, load more (Section Rendering API)
 * Parts marked [data-plp-part="key"] are swapped from the server response.
 */
const OSP_DISCOUNT_SORT = 'discount-descending';
const OSP_MAX_PAGES = 15; // safety cap when loading a whole collection to sort by discount

if (!customElements.get('oshea-plp')) {
  class OsheaPlp extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.pendingDoc = null;
      this.pendingUrl = null;
      this.pagesLoaded = 1;
      this.controller = null;
      this.sheet = this.querySelector('[data-osp-sheet]');

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      window.addEventListener('popstate', () => this.load(window.location.href, { push: false }));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.sheet && !this.sheet.hidden) this.closeSheet();
      });

      this.initDescription();
      this.syncPriceSliders(this);

      // Deep link / refresh on ?sort_by=discount-descending — Shopify can't sort by
      // discount, so re-order client-side as soon as the element is live.
      if (this.currentSort() === OSP_DISCOUNT_SORT) {
        this.loadDiscount(window.location.href, { push: false });
      } else {
        this.restoreScroll();
      }
    }

    /* ---------------- URL helpers ---------------- */
    sectionUrl(url) {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', this.sectionId);
      return u.toString();
    }

    cleanUrl(url) {
      const u = new URL(url, window.location.origin);
      u.searchParams.delete('section_id');
      u.searchParams.delete('page');
      return u.pathname + (u.search ? u.search : '');
    }

    currentSort() {
      return new URL(window.location.href).searchParams.get('sort_by') || '';
    }

    urlFromForm(form, sortOverride) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      const sort = sortOverride !== undefined ? sortOverride : this.currentSort();
      if (sort) params.set('sort_by', sort);
      const qs = params.toString();
      return window.location.pathname + (qs ? `?${qs}` : '');
    }

    /* ---------------- fetching ---------------- */
    async fetchDoc(url) {
      const key = this.sectionUrl(url);
      if (this.cache.has(key)) return this.parse(this.cache.get(key));
      this.controller?.abort();
      this.controller = new AbortController();
      const res = await fetch(key, { signal: this.controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      this.cache.set(key, html);
      return this.parse(html);
    }

    parse(html) {
      return new DOMParser().parseFromString(html, 'text/html');
    }

    setLoading(on) {
      this.classList.toggle('is-loading', on);
    }

    sortOf(url) {
      return new URL(url, window.location.origin).searchParams.get('sort_by') || '';
    }

    async load(url, opts = {}) {
      if (this.sortOf(url) === OSP_DISCOUNT_SORT) return this.loadDiscount(url, opts);
      return this.loadStandard(url, opts);
    }

    async loadStandard(url, { push = true, keepSheet = false } = {}) {
      this.setLoading(true);
      try {
        const doc = await this.fetchDoc(url);
        this.render(doc);
        this.pagesLoaded = 1;
        if (push) history.pushState({ osp: true }, '', this.cleanUrl(url));
        if (!keepSheet) this.closeSheet();
        this.track('plp_filter_apply', { plp_url: this.cleanUrl(url) });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[oshea-plp]', err);
          window.location.href = this.cleanUrl(url);
        }
      } finally {
        this.setLoading(false);
      }
    }

    /* ---------------- discount sort (client-side) ----------------
     * Shopify has no discount sort, so we render the same filtered result set in a
     * stable server order (best-selling), pull every page, then order by the
     * per-card data-osp-disc value. Ties keep their best-selling position.
     */
    nextUrlFrom(doc) {
      const source = doc.querySelector('oshea-plp') || doc;
      const btn = source.querySelector('[data-osp-loadmore]');
      return btn ? btn.dataset.next : null;
    }

    labelForSort(value) {
      const opt = this.querySelector(`[data-osp-sort] option[value="${value}"]`);
      return opt ? opt.textContent.trim() : '';
    }

    syncSortUi(value) {
      const select = this.querySelector('[data-osp-sort]');
      if (select) select.value = value;
      this.querySelectorAll('input[name="osp_sort"]').forEach((radio) => {
        const on = radio.value === value;
        radio.checked = on;
        radio.closest('.osp-sortopt')?.classList.toggle('is-on', on);
      });
      const label = this.labelForSort(value);
      const text = this.querySelector('.osp-bar__sort-text b');
      if (text && label) text.textContent = label;
    }

    async loadDiscount(url, { push = true } = {}) {
      this.setLoading(true);
      const target = new URL(url, window.location.origin);
      const base = new URL(url, window.location.origin);
      // Stable server order to re-sort from: best-selling on a collection,
      // relevance on search (where best-selling is not a valid sort).
      base.searchParams.set('sort_by', this.dataset.ospBaseSort || 'best-selling');
      base.searchParams.delete('page');

      try {
        const doc = await this.fetchDoc(base.toString());
        this.render(doc);

        const grid = this.querySelector('[data-plp-part="grid"]');
        const items = Array.from(grid.querySelectorAll('.osp-item'));

        let next = this.nextUrlFrom(doc);
        let page = 1;
        while (next && page < OSP_MAX_PAGES) {
          page += 1;
          const more = await this.fetchDoc(next);
          (more.querySelector('oshea-plp') || more)
            .querySelectorAll('[data-plp-part="grid"] > .osp-item')
            .forEach((li) => items.push(li));
          next = this.nextUrlFrom(more);
        }

        if (items.length) {
          // The server's "Best match" flag belongs to relevance order, not this one.
          items.forEach((li) => {
            li.classList.remove('oss-best');
            li.querySelector('.oss-best__tag')?.remove();
          });
          items.forEach((li, i) => (li.dataset.ospIdx = i));
          items.sort(
            (a, b) =>
              Number(b.dataset.ospDisc || 0) - Number(a.dataset.ospDisc || 0) ||
              Number(a.dataset.ospIdx) - Number(b.dataset.ospIdx)
          );
          grid.innerHTML = '';
          items.forEach((li) => grid.appendChild(li));

          const pager = this.querySelector('[data-plp-part="pager"]');
          if (pager) {
            const stem = this.dataset.ospNoun || 'product';
            const noun = items.length === 1 ? stem : `${stem}s`;
            pager.innerHTML =
              `<p class="osp-pager__text">Showing <b>${items.length}</b> of ${items.length} ${noun}</p>` +
              '<div class="osp-pager__bar"><i style="width: 100%;"></i></div>';
          }
        }

        this.pagesLoaded = 1;
        this.syncSortUi(OSP_DISCOUNT_SORT);
        if (push) history.pushState({ osp: true }, '', this.cleanUrl(target.toString()));
        this.closeSheet();
        this.track('plp_sort', { sort_by: OSP_DISCOUNT_SORT, products: items.length });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[oshea-plp]', err);
          this.setLoading(false);
          return this.loadStandard(base.toString());
        }
      } finally {
        this.setLoading(false);
      }
    }

    render(doc, { only } = {}) {
      const source = doc.querySelector('oshea-plp') || doc;
      this.querySelectorAll('[data-plp-part]').forEach((target) => {
        const key = target.dataset.plpPart;
        if (only && !only.includes(key)) return;
        const incoming = source.querySelector(`[data-plp-part="${key}"]`);
        if (!incoming) return;

        if (key === 'sidebar' || key === 'sheet') {
          const openState = this.captureOpen(target);
          const scroller = target.closest('.osp-sheet__body, .osp-side__inner');
          const scrollTop = scroller ? scroller.scrollTop : 0;
          target.innerHTML = incoming.innerHTML;
          this.applyOpen(target, openState);
          if (scroller) scroller.scrollTop = scrollTop;
          this.syncPriceSliders(target);
        } else {
          target.innerHTML = incoming.innerHTML;
        }
        if (key === 'meta') {
          target.dataset.count = incoming.dataset.count;
          target.dataset.active = incoming.dataset.active;
        }
      });
    }

    captureOpen(root) {
      const state = {};
      root.querySelectorAll('[data-osp-group]').forEach((d) => {
        state[d.dataset.ospGroup] = { open: d.open, long: d.querySelector('.osp-opts')?.classList.contains('is-expanded') };
      });
      return state;
    }

    applyOpen(root, state) {
      root.querySelectorAll('[data-osp-group]').forEach((d) => {
        const s = state[d.dataset.ospGroup];
        if (!s) return;
        d.open = s.open || d.open;
        if (s.long) {
          d.querySelector('.osp-opts')?.classList.add('is-expanded');
          const btn = d.querySelector('[data-osp-showmore]');
          if (btn) btn.textContent = 'Show less';
        }
      });
    }

    /* ---------------- events ---------------- */
    onClick(e) {
      const link = e.target.closest('a[data-osp-link]');
      if (link && this.contains(link)) {
        e.preventDefault();
        this.load(link.href);
        return;
      }

      const opener = e.target.closest('[data-osp-open]');
      if (opener) {
        this.openSheet(opener.dataset.ospOpen);
        return;
      }

      const tab = e.target.closest('[data-osp-tab]');
      if (tab) {
        this.showPane(tab.dataset.ospTab);
        return;
      }

      if (e.target.closest('[data-osp-close]')) {
        this.closeSheet();
        return;
      }

      if (e.target.closest('[data-osp-apply]')) {
        this.applySheet();
        return;
      }

      if (e.target.closest('[data-osp-sheet-clear]')) {
        const form = this.querySelector('[data-osp-form="sheet"]');
        form.querySelectorAll('input[type="checkbox"]').forEach((i) => (i.checked = false));
        form.querySelectorAll('input[type="number"]').forEach((i) => (i.value = ''));
        this.previewSheet();
        return;
      }

      const more = e.target.closest('[data-osp-showmore]');
      if (more) {
        const opts = more.previousElementSibling;
        const expanded = opts.classList.toggle('is-expanded');
        more.textContent = expanded ? 'Show less' : more.textContent.replace('Show less', 'Show all');
        if (!expanded) more.textContent = 'Show all';
        return;
      }

      const loadMore = e.target.closest('[data-osp-loadmore]');
      if (loadMore) {
        this.loadMore(loadMore);
        return;
      }

      const toggle = e.target.closest('[data-osp-desc-toggle]');
      if (toggle) {
        const desc = toggle.closest('[data-osp-desc]');
        const open = desc.classList.toggle('is-open');
        toggle.textContent = open ? 'Read less' : 'Read more';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        return;
      }

      const productLink = e.target.closest('.osp-item a[href*="/products/"]');
      if (productLink) this.saveScroll();
    }

    onChange(e) {
      const t = e.target;

      if (t.matches('[data-osp-sort]')) {
        const form = this.querySelector('[data-osp-form="sidebar"]') || this.querySelector('[data-osp-form="sheet"]');
        this.track('plp_sort', { sort_by: t.value });
        this.load(this.urlFromForm(form, t.value));
        return;
      }

      if (t.name === 'osp_sort') {
        const form = this.querySelector('[data-osp-form="sheet"]');
        this.track('plp_sort', { sort_by: t.value });
        this.load(this.urlFromForm(form, t.value));
        return;
      }

      const form = t.closest('[data-osp-form]');
      if (!form) return;
      if (t.type === 'checkbox') t.closest('.osp-opt')?.classList.toggle('is-on', t.checked);
      if (t.type === 'number') return; // handled on input (debounced)

      if (form.dataset.ospForm === 'sidebar') {
        this.load(this.urlFromForm(form));
      } else {
        this.previewSheet();
      }
    }

    onInput(e) {
      const t = e.target;
      const priceBox = t.closest('[data-osp-price]');
      if (!priceBox) return;

      if (t.matches('[data-osp-range]')) {
        const minR = priceBox.querySelector('[data-osp-range="min"]');
        const maxR = priceBox.querySelector('[data-osp-range="max"]');
        if (+minR.value > +maxR.value) {
          if (t === minR) minR.value = maxR.value;
          else maxR.value = minR.value;
        }
        const max = +priceBox.dataset.max;
        priceBox.querySelector('[data-osp-price-input="min"]').value = +minR.value > 0 ? minR.value : '';
        priceBox.querySelector('[data-osp-price-input="max"]').value = +maxR.value < max ? maxR.value : '';
      }
      this.syncPriceSliders(priceBox);

      clearTimeout(this.priceTimer);
      this.priceTimer = setTimeout(() => {
        const form = priceBox.closest('[data-osp-form]');
        if (form.dataset.ospForm === 'sidebar') this.load(this.urlFromForm(form));
        else this.previewSheet();
      }, 600);
    }

    syncPriceSliders(root) {
      root.querySelectorAll('[data-osp-price]').forEach((box) => {
        const max = +box.dataset.max || 1;
        const minI = box.querySelector('[data-osp-price-input="min"]');
        const maxI = box.querySelector('[data-osp-price-input="max"]');
        const minR = box.querySelector('[data-osp-range="min"]');
        const maxR = box.querySelector('[data-osp-range="max"]');
        const lo = minI.value === '' ? 0 : Math.min(+minI.value, max);
        const hi = maxI.value === '' ? max : Math.min(+maxI.value, max);
        if (document.activeElement !== minR) minR.value = lo;
        if (document.activeElement !== maxR) maxR.value = hi;
        const fill = box.querySelector('[data-osp-price-fill]');
        if (fill) {
          fill.style.left = `${(lo / max) * 100}%`;
          fill.style.right = `${100 - (hi / max) * 100}%`;
        }
      });
    }

    /* ---------------- sheet ---------------- */
    openSheet(pane) {
      if (!this.sheet) return;
      this.showPane(pane || 'filters');
      this.sheet.hidden = false;
      this.lastFocus = document.activeElement;
      document.documentElement.classList.add('osp-lock');
      requestAnimationFrame(() => this.sheet.classList.add('is-open'));
      this.sheet.querySelector('.osp-sheet__close')?.focus({ preventScroll: true });
    }

    closeSheet() {
      if (!this.sheet || this.sheet.hidden) return;
      this.sheet.classList.remove('is-open');
      document.documentElement.classList.remove('osp-lock');
      setTimeout(() => {
        this.sheet.hidden = true;
      }, 260);
      this.pendingDoc = null;
      this.pendingUrl = null;
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    showPane(pane) {
      this.querySelectorAll('[data-osp-tab]').forEach((t) => {
        const on = t.dataset.ospTab === pane;
        t.classList.toggle('is-on', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.querySelectorAll('[data-osp-pane]').forEach((p) => (p.hidden = p.dataset.ospPane !== pane));
      this.querySelectorAll('[data-osp-pane-foot]').forEach((p) => (p.hidden = p.dataset.ospPaneFoot !== pane));
    }

    async previewSheet() {
      const form = this.querySelector('[data-osp-form="sheet"]');
      const url = this.urlFromForm(form);
      const label = this.querySelector('[data-osp-apply-label]');
      if (label) label.textContent = 'Updating…';
      try {
        const doc = await this.fetchDoc(url);
        this.pendingDoc = doc;
        this.pendingUrl = url;
        this.render(doc, { only: ['sheet', 'apply-label'] });
      } catch (err) {
        if (err.name !== 'AbortError') console.error('[oshea-plp]', err);
      }
    }

    applySheet() {
      if (this.pendingUrl && this.sortOf(this.pendingUrl) === OSP_DISCOUNT_SORT) {
        const url = this.pendingUrl;
        this.pendingDoc = null;
        this.pendingUrl = null;
        this.closeSheet();
        this.scrollToTopOfGrid();
        this.loadDiscount(url);
        return;
      }
      if (this.pendingDoc && this.pendingUrl) {
        this.render(this.pendingDoc);
        history.pushState({ osp: true }, '', this.cleanUrl(this.pendingUrl));
        this.pagesLoaded = 1;
        this.track('plp_filter_apply', { plp_url: this.cleanUrl(this.pendingUrl) });
        this.closeSheet();
        this.scrollToTopOfGrid();
      } else {
        this.closeSheet();
      }
    }

    scrollToTopOfGrid() {
      const bar = this.querySelector('.osp-bar');
      if (!bar) return;
      const top = bar.getBoundingClientRect().top + window.scrollY - 80;
      if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
    }

    /* ---------------- load more ---------------- */
    async loadMore(btn) {
      const next = btn.dataset.next;
      if (!next || btn.disabled) return;
      btn.disabled = true;
      btn.classList.add('is-busy');
      btn.textContent = 'Loading…';
      try {
        const res = await fetch(this.sectionUrl(next));
        const doc = this.parse(await res.text());
        const source = doc.querySelector('oshea-plp') || doc;
        const grid = this.querySelector('[data-plp-part="grid"]');
        source.querySelectorAll('[data-plp-part="grid"] > .osp-item').forEach((li) => grid.appendChild(li));
        const pager = this.querySelector('[data-plp-part="pager"]');
        const newPager = source.querySelector('[data-plp-part="pager"]');
        if (pager && newPager) pager.innerHTML = newPager.innerHTML;
        this.pagesLoaded += 1;
        this.track('plp_load_more', { page: this.pagesLoaded });
      } catch (err) {
        console.error('[oshea-plp]', err);
        window.location.href = next;
      }
    }

    /* ---------------- scroll restore ---------------- */
    storageKey() {
      return `osp:${window.location.pathname}${window.location.search}`;
    }

    saveScroll() {
      try {
        sessionStorage.setItem(this.storageKey(), JSON.stringify({ pages: this.pagesLoaded, y: window.scrollY }));
      } catch (e) {}
    }

    async restoreScroll() {
      let data = null;
      try {
        data = JSON.parse(sessionStorage.getItem(this.storageKey()) || 'null');
      } catch (e) {}
      if (!data) return;
      const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (!nav || nav.type !== 'back_forward') return;
      try {
        sessionStorage.removeItem(this.storageKey());
      } catch (e) {}

      for (let i = 1; i < data.pages; i++) {
        const btn = this.querySelector('[data-osp-loadmore]');
        if (!btn) break;
        await this.loadMore(btn);
      }
      requestAnimationFrame(() => window.scrollTo(0, data.y));
    }

    /* ---------------- description clamp ---------------- */
    initDescription() {
      const desc = this.querySelector('[data-osp-desc]');
      if (!desc) return;
      const text = desc.querySelector('.osp-desc__text');
      requestAnimationFrame(() => {
        if (text.scrollHeight <= text.clientHeight + 2) desc.classList.add('is-short');
      });
    }

    track(event, params) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event }, params));
      } catch (e) {}
    }
  }

  customElements.define('oshea-plp', OsheaPlp);
}
